import React from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronDown } from "lucide-react";
import StoreButtons from "./StoreButtons";

/**
 * HeroSection — full-viewport image hero.
 *
 * Single dramatic background image with desaturated dark treatment (CSS
 * grayscale + brightness + contrast) and a dark vignette gradient so the
 * headline + search bar stay legible. A subtle slate/forest radial accent
 * shimmers through to match the rest of the redesigned sections.
 *
 * The image URL lives in HERO_IMAGE — swap it to point at the Higgsfield
 * render (or any GCS URL) once it's ready.
 */

// Higgsfield Soul v2 — group run, no-text directive, dark editorial mood.
// Job: d0b7ac11-4b0e-4646-8a84-dcf27f562abe · 2048×1152 · 16:9
const HERO_IMAGE =
  "https://d8j0ntlcm91z4.cloudfront.net/user_3EGkgzs42X1SHMn8pRo8uNa9a22/hf_20260602_172544_d0b7ac11-4b0e-4646-8a84-dcf27f562abe.png";

export default function HeroSection({
  searchQuery = "",
  onSearchQueryChange,
  onSearch,
  showGuestLocationPrompt = false,
  locationPromptLoading = false,
  onSkipLocation,
}) {
  const navigate = useNavigate();

  const submit = () => {
    const q = (searchQuery || "").trim();
    // When the host page wants to filter in place (homepage), hand it the city
    // so it can update the runs + clubs sections below — no navigation.
    if (onSearch) {
      onSearch(q);
      return;
    }
    navigate(q ? `/runclubs?city=${encodeURIComponent(q)}` : "/runclubs");
  };

  return (
    <section className="relative flex min-h-screen items-center justify-center overflow-hidden">
      {/* Background image — fills the full viewport at every aspect ratio.
          Use `cover` so portrait phones don't letterbox the 16:9 source. */}
      <div
        className="absolute inset-0 bg-no-repeat"
        style={{
          backgroundImage: `url('${HERO_IMAGE}')`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
          // Desaturated + dark treatment — color preserved, slight brightness lift
          filter: "grayscale(45%) brightness(0.75) contrast(1.08)",
        }}
      />

      {/* Dark vignette — keeps text legible (lightened middle for the brighter source image) */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/65" />

      {/* Subtle brand-accent radial wash — slate top-right, forest bottom-left */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, rgba(120,127,154,0.18), transparent 55%), radial-gradient(circle at 20% 80%, rgba(58,106,76,0.12), transparent 60%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-3xl px-4 pt-20 text-center sm:px-6 sm:pt-24 md:pt-28">
        {/* Display headline — Creato Display Black to match the rest of the
            brand. Italic on the verb ("Find") gives the editorial moment
            without breaking the typeface family. */}
        <h1
          className="mb-4 text-white"
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontWeight: 400,
            fontSize: "clamp(3rem, 8.5vw, 6.25rem)",
            lineHeight: 0.95,
            letterSpacing: "-0.005em",
          }}
        >
          <span style={{ fontStyle: "italic" }}>Find</span> your next run.
        </h1>

        {/* Lede */}
        <p className="mb-7 mx-auto max-w-xl font-runcards text-white/80 text-runcards-md sm:text-runcards-lg">
          Group runs near you. Real clubs. Real community. Never run alone.
        </p>

        {/* Optional guest geo-prompt — same liquid glass material */}
        {showGuestLocationPrompt && (
          <div
            className="mx-auto mb-6 flex max-w-md flex-col items-center justify-center gap-3 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 sm:flex-row"
            style={{
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 32px rgba(0,0,0,0.25)",
            }}
          >
            <span className="flex items-center gap-2 text-center font-runcards text-runcards-sm text-white/95 sm:text-left">
              <MapPin className="h-4 w-4 flex-shrink-0" aria-hidden />
              {locationPromptLoading ? "Getting your location…" : "See runs near you"}
            </span>
            {onSkipLocation && (
              <button
                type="button"
                onClick={onSkipLocation}
                disabled={locationPromptLoading}
                className="flex-shrink-0 rounded-md border border-white/35 px-3 py-1 text-sm text-white transition-colors hover:bg-white/15 disabled:opacity-50"
              >
                Not now
              </button>
            )}
          </div>
        )}

        {/* Search — liquid glass treatment, clubs-only */}
        <form
          className="mx-auto flex w-full max-w-lg flex-col items-center gap-2 sm:flex-row sm:gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="relative w-full">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/65"
              aria-hidden
            />
            <input
              type="search"
              placeholder="Search by club name or city — e.g. Midnight Runners, Miami…"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange?.(e.target.value)}
              aria-label="Search clubs by name or city"
              className="w-full rounded-xl border border-white/25 bg-white/10 py-2.5 pl-9 pr-3 font-runcards text-xs text-white placeholder:text-white/55 transition-all focus:border-white/50 focus:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/25 sm:py-3 sm:text-runcards-sm"
              style={{
                backdropFilter: "blur(20px) saturate(180%)",
                WebkitBackdropFilter: "blur(20px) saturate(180%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 0 0 1px rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.25)",
              }}
            />
          </div>
          <button
            type="submit"
            aria-label="Search"
            className="group relative w-full flex-shrink-0 overflow-hidden rounded-xl border border-white/35 bg-white/20 px-5 py-2.5 font-runcards-bold text-xs text-white transition-all hover:scale-[1.02] hover:bg-white/30 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 sm:w-auto sm:py-3 sm:text-runcards-sm"
            style={{
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.25)",
            }}
          >
            {/* Top-edge specular highlight — gives the "wet glass" look */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-2xl opacity-60"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.25), transparent)",
              }}
            />
            <span className="relative">Search</span>
          </button>
        </form>

        {/* App store CTAs */}
        <div className="mt-6 flex justify-center">
          {/* Side-by-side on every width. On phones the row fills the width
              with two equal buttons; on desktop they sit at natural width. */}
          <StoreButtons
            variant="glass"
            className="w-full !flex-row [&>a]:flex-1 sm:w-auto sm:[&>a]:flex-none"
          />
        </div>
      </div>

      {/* Scroll cue */}
      <button
        type="button"
        aria-label="Scroll to runs nearby"
        onClick={() => {
          if (typeof window !== "undefined") {
            window.scrollBy({ top: window.innerHeight - 80, behavior: "smooth" });
          }
        }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-white/70 transition-colors hover:text-white"
      >
        <span className="block animate-bounce">
          <ChevronDown className="h-8 w-8" />
        </span>
      </button>
    </section>
  );
}
